import React, { useEffect, useState } from 'react';
import {
    Box,
    Container,
    Typography,
    TextField,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Paper,
    Chip,
    Alert,
    FormHelperText,
    IconButton
} from '@mui/material';
import {
    ArrowBack,
    Add as AddIcon,
    Close as CloseIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import TitleBar from '../../components/TitleBar/TitleBar';
import { 
    getEntities, 
    createReviewQuestion, 
    getReviewEntityTypes, 
    ReviewEntityType,
    getReviewFrequencies,
    ReviewFrequency,
    getReviewDimensions,
    ReviewDimension
} from '../../services/api';

interface Entity {
    id: string;
    name: string;
}

interface FormData {
    review_question_text: string;
    description: string;
    review_dimension: string;
    review_frequency: string;
    answer_type: string;
    answer_options: string[];
    review_entity_type: string;
    action_required: string;
    default_value: string;
    ideal_value: string;
}

const AddQuestion = () => {
    const navigate = useNavigate();
    const [dimensions, setDimensions] = useState<ReviewDimension[]>([]);
    const [frequencies, setFrequencies] = useState<ReviewFrequency[]>([]);
    const [answerTypes, setAnswerTypes] = useState<Entity[]>([]);
    const [entityTypes, setEntityTypes] = useState<ReviewEntityType[]>([]);
    const [newOption, setNewOption] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);

    const [formData, setFormData] = useState<FormData>({
        review_question_text: '',
        description: '',
        review_dimension: '',
        review_frequency: '',
        answer_type: '',
        answer_options: [],
        review_entity_type: '',
        action_required: '',
        default_value: '',
        ideal_value: ''
    });

    const [formErrors, setFormErrors] = useState<Partial<Record<keyof FormData, string>>>({});

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [dimensionsRes, frequenciesRes, answerTypesRes, entityTypesRes] = await Promise.all([
                    getReviewDimensions(),
                    getReviewFrequencies(),
                    getEntities('AnswerType'),
                    getReviewEntityTypes()
                ]);

                setDimensions(dimensionsRes.data);
                setFrequencies(frequenciesRes.data);
                setAnswerTypes(answerTypesRes.data);
                setEntityTypes(entityTypesRes.data);
            } catch (err) {
                setError('Failed to load form data');
            }
        };

        fetchData();
    }, []);

    const handleInputChange = (field: keyof FormData, value: string | string[]) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        setFormErrors(prev => ({ ...prev, [field]: '' }));
        
        // Clear answer options when answer type changes
        if (field === 'answer_type' && !['SELECT', 'MULTI-SELECT'].includes(value as string)) {
            setFormData(prev => ({ ...prev, answer_options: [] }));
        }
        
        // Clear default_value and ideal_value when answer type changes
        if (field === 'answer_type') {
            setFormData(prev => ({ ...prev, default_value: '', ideal_value: '' }));
        }
    };

    const handleAddOption = () => {
        if (newOption.trim()) {
            setFormData(prev => ({
                ...prev,
                answer_options: [...prev.answer_options, newOption.trim()]
            }));
            setNewOption('');
        }
    };

    const handleRemoveOption = (index: number) => {
        setFormData(prev => ({
            ...prev,
            answer_options: prev.answer_options.filter((_, i) => i !== index)
        }));
    };

    const getValidOptionsForAnswerType = (answerType: string): string[] => {
        switch (answerType) {
            case 'YES-NO':
                return ['YES', 'NO'];
            case 'TRUE-FALSE':
                return ['TRUE', 'FALSE'];
            case 'SELECT':
            case 'MULTI-SELECT':
                return formData.answer_options;
            default:
                return [];
        }
    };

    const shouldShowValueSelectors = (answerType: string): boolean => {
        return ['YES-NO', 'TRUE-FALSE', 'SELECT', 'MULTI-SELECT'].includes(answerType);
    };

    const validateForm = (): boolean => {
        const errors: Partial<Record<keyof FormData, string>> = {};
        
        if (!formData.review_question_text) {
            errors.review_question_text = 'Question text is required';
        }
        if (!formData.description) {
            errors.description = 'Description is required';
        }
        if (!formData.review_dimension) {
            errors.review_dimension = 'Review dimension is required';
        }
        if (!formData.review_frequency) {
            errors.review_frequency = 'Review frequency is required';
        }
        if (!formData.answer_type) {
            errors.answer_type = 'Answer type is required';
        }
        if (!formData.review_entity_type) {
            errors.review_entity_type = 'Review entity type is required';
        }
        if (['SELECT', 'MULTI-SELECT'].includes(formData.answer_type) && formData.answer_options.length === 0) {
            errors.answer_options = 'At least one answer option is required';
        }

        // Validate default_value and ideal_value based on answer_type
        if (shouldShowValueSelectors(formData.answer_type)) {
            const validOptions = getValidOptionsForAnswerType(formData.answer_type);
            
            if (formData.default_value && !validOptions.includes(formData.default_value)) {
                errors.default_value = `Default value must be one of: ${validOptions.join(', ')}`;
            }
            
            if (formData.ideal_value) {
                if (['SELECT', 'MULTI-SELECT'].includes(formData.answer_type)) {
                    const idealValues = formData.ideal_value.split(',').map(v => v.trim()).filter(Boolean);
                    const invalid = idealValues.filter(v => !validOptions.includes(v));
                    if (invalid.length > 0) {
                        errors.ideal_value = `Ideal values must be from options: ${validOptions.join(', ')}`;
                    }
                } else if (!validOptions.includes(formData.ideal_value)) {
                    errors.ideal_value = `Ideal value must be one of: ${validOptions.join(', ')}`;
                }
            }
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;

        setLoading(true);
        setError('');
        setSuccess('');

        try {
            await createReviewQuestion({
                ...formData,
                review_question_type: 'CUSTOM'
            });
            setSuccess('Question added successfully');
            setTimeout(() => navigate(-1), 2000);
        } catch (err) {
            setError('Failed to add question');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box>
            <TitleBar />
            <Container maxWidth="md" sx={{ mt: 4 }}>
                <Paper sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                        <Typography variant="h4">Add Question</Typography>
                        <Button
                            variant="outlined"
                            startIcon={<ArrowBack />}
                            onClick={() => navigate(-1)}
                        >
                            Back
                        </Button>
                    </Box>

                    {(error || success) && (
                        <Alert 
                            severity={error ? 'error' : 'success'} 
                            sx={{ mb: 3 }}
                            onClose={() => error ? setError('') : setSuccess('')}
                        >
                            {error || success}
                        </Alert>
                    )}

                    <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <TextField
                            label="Question Text *"
                            multiline
                            rows={3}
                            value={formData.review_question_text}
                            onChange={(e) => handleInputChange('review_question_text', e.target.value)}
                            error={!!formErrors.review_question_text}
                            helperText={formErrors.review_question_text}
                        />

                        <TextField
                            label="Description *"
                            multiline
                            rows={3}
                            value={formData.description}
                            onChange={(e) => handleInputChange('description', e.target.value)}
                            error={!!formErrors.description}
                            helperText={formErrors.description}
                        />

                        <FormControl error={!!formErrors.review_dimension}>
                            <InputLabel>Review Dimension *</InputLabel>
                            <Select
                                value={formData.review_dimension}
                                label="Review Dimension *"
                                onChange={(e) => handleInputChange('review_dimension', e.target.value)}
                            >
                                {dimensions.map((dim) => (
                                    <MenuItem key={dim.id} value={dim.name}>
                                        {dim.name}
                                    </MenuItem>
                                ))}
                            </Select>
                            {formErrors.review_dimension && (
                                <FormHelperText>{formErrors.review_dimension}</FormHelperText>
                            )}
                        </FormControl>

                        <FormControl error={!!formErrors.review_frequency}>
                            <InputLabel>Review Frequency *</InputLabel>
                            <Select
                                value={formData.review_frequency}
                                label="Review Frequency *"
                                onChange={(e) => handleInputChange('review_frequency', e.target.value)}
                            >
                                {frequencies.map((freq) => (
                                    <MenuItem key={freq.id} value={freq.name}>
                                        {freq.display_label}
                                    </MenuItem>
                                ))}
                            </Select>
                            {formErrors.review_frequency && (
                                <FormHelperText>{formErrors.review_frequency}</FormHelperText>
                            )}
                        </FormControl>

                        <FormControl error={!!formErrors.answer_type}>
                            <InputLabel>Answer Type *</InputLabel>
                            <Select
                                value={formData.answer_type}
                                label="Answer Type *"
                                onChange={(e) => handleInputChange('answer_type', e.target.value)}
                            >
                                {answerTypes.map((type) => (
                                    <MenuItem key={type.id} value={type.name}>
                                        {type.name}
                                    </MenuItem>
                                ))}
                            </Select>
                            {formErrors.answer_type && (
                                <FormHelperText>{formErrors.answer_type}</FormHelperText>
                            )}
                        </FormControl>

                        {['SELECT', 'MULTI-SELECT'].includes(formData.answer_type) && (
                            <Box>
                                <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                                    <TextField
                                        label="Add Answer Option"
                                        value={newOption}
                                        onChange={(e) => setNewOption(e.target.value)}
                                        error={!!formErrors.answer_options}
                                        fullWidth
                                    />
                                    <Button
                                        variant="contained"
                                        onClick={handleAddOption}
                                        disabled={!newOption.trim()}
                                    >
                                        <AddIcon />
                                    </Button>
                                </Box>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                    {formData.answer_options.map((option, index) => (
                                        <Chip
                                            key={index}
                                            label={option}
                                            onDelete={() => handleRemoveOption(index)}
                                            deleteIcon={<CloseIcon />}
                                        />
                                    ))}
                                </Box>
                                {formErrors.answer_options && (
                                    <FormHelperText error>{formErrors.answer_options}</FormHelperText>
                                )}
                            </Box>
                        )}

                        <FormControl fullWidth margin="normal" error={!!formErrors.review_entity_type}>
                            <InputLabel>Review Entity Type *</InputLabel>
                            <Select
                                value={formData.review_entity_type}
                                onChange={(e) => handleInputChange('review_entity_type', e.target.value)}
                                label="Review Entity Type *"
                            >
                                {entityTypes.map((type) => (
                                    <MenuItem key={type.id} value={type.name}>
                                        {type.display_label}
                                    </MenuItem>
                                ))}
                            </Select>
                            {formErrors.review_entity_type && (
                                <FormHelperText>{formErrors.review_entity_type}</FormHelperText>
                            )}
                        </FormControl>

                        <TextField
                            label="Action Required (Optional)"
                            multiline
                            rows={3}
                            value={formData.action_required}
                            onChange={(e) => handleInputChange('action_required', e.target.value)}
                        />

                        {/* Default Value and Ideal Value fields - only show for specific answer types */}
                        {shouldShowValueSelectors(formData.answer_type) && (
                            <>
                                <FormControl error={!!formErrors.default_value}>
                                    <InputLabel>Default Value (Optional)</InputLabel>
                                    <Select
                                        value={formData.default_value}
                                        label="Default Value (Optional)"
                                        onChange={(e) => handleInputChange('default_value', e.target.value)}
                                    >
                                        <MenuItem value="">
                                            <em>None</em>
                                        </MenuItem>
                                        {getValidOptionsForAnswerType(formData.answer_type).map((option) => (
                                            <MenuItem key={option} value={option}>
                                                {option}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    {formErrors.default_value && (
                                        <FormHelperText>{formErrors.default_value}</FormHelperText>
                                    )}
                                </FormControl>

                                <FormControl error={!!formErrors.ideal_value}>
                                    <InputLabel>Ideal Value (Optional)</InputLabel>
                                    {['SELECT', 'MULTI-SELECT'].includes(formData.answer_type) ? (
                                        <Select
                                            multiple
                                            value={formData.ideal_value ? formData.ideal_value.split(',').map(v => v.trim()).filter(Boolean) : []}
                                            label="Ideal Value (Optional)"
                                            onChange={(e) => handleInputChange('ideal_value', (e.target.value as string[]).join(','))}
                                            renderValue={(selected) => (selected as string[]).join(', ')}
                                        >
                                            {getValidOptionsForAnswerType(formData.answer_type).map((option) => (
                                                <MenuItem key={option} value={option}>
                                                    {option}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    ) : (
                                        <Select
                                            value={formData.ideal_value}
                                            label="Ideal Value (Optional)"
                                            onChange={(e) => handleInputChange('ideal_value', e.target.value)}
                                        >
                                            <MenuItem value="">
                                                <em>None</em>
                                            </MenuItem>
                                            {getValidOptionsForAnswerType(formData.answer_type).map((option) => (
                                                <MenuItem key={option} value={option}>
                                                    {option}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    )}
                                    {formErrors.ideal_value && (
                                        <FormHelperText>{formErrors.ideal_value}</FormHelperText>
                                    )}
                                </FormControl>
                            </>
                        )}

                        {/* For other answer types, show text fields */}
                        {!shouldShowValueSelectors(formData.answer_type) && formData.answer_type && (
                            <>
                                <TextField
                                    label="Default Value (Optional)"
                                    value={formData.default_value}
                                    onChange={(e) => handleInputChange('default_value', e.target.value)}
                                    error={!!formErrors.default_value}
                                    helperText={formErrors.default_value || 'Enter a default value for this question type'}
                                />

                                <TextField
                                    label="Ideal Value (Optional)"
                                    value={formData.ideal_value}
                                    onChange={(e) => handleInputChange('ideal_value', e.target.value)}
                                    error={!!formErrors.ideal_value}
                                    helperText={formErrors.ideal_value || 'Enter an ideal value for this question type'}
                                />
                            </>
                        )}

                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
                            <Button
                                variant="contained"
                                onClick={handleSubmit}
                                disabled={loading}
                            >
                                {loading ? 'Adding...' : 'Add Question'}
                            </Button>
                        </Box>
                    </Box>
                </Paper>
            </Container>
        </Box>
    );
};

export default AddQuestion;